export const round2 = (num) =>
  Math.round((Number(num) + Number.EPSILON) * 100) / 100; // helper function to round a number to two decimal places

export const toNumber = (v) => {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}; // a helper function to ensure the input will be a valid number

const grossToNet = (gross, vatRate) => {
  const vat = Math.max(0, toNumber(vatRate));
  const divisor = 1 + vat / 100;
  const net = divisor > 0 ? gross / divisor : gross;
  return round2(net);
}; // helper function: calculates the net out of gross for a given VAT rate

export const itemsGross = (cartItems) =>
  round2(
    cartItems.reduce(
      (sum, c) => sum + round2(toNumber(c.unitGross) * toNumber(c.quantity)),
      0
    )
  ); // Brutto-Zwischensumme (Summe aus bruttoStk * menge): the checkout's items total, which the cart page's subtotal shows too (it summed the lines unrounded until chain 10, Oct 2026, which formatEuro rounded away for the display: the same figure, from one place)

export const calculateOrderTotals = (cartItems, shippingPrice) => {
  const itemsGrossSum = itemsGross(cartItems);

  const shippingGross = round2(toNumber(shippingPrice)); // Brutto Versand
  const totalGross = round2(itemsGrossSum + shippingGross); // Brutto Gesamt

  const itemsNet = round2(
    cartItems.reduce((sum, c) => {
      const nettoStk = round2(toNumber(c.unitNet)); // netto pro Stück (kommt aus DB): multiplied per piece instead of taken from the line gross, so refunds of any number of pieces add up to the order's net exactly
      const nettoZeile = round2(nettoStk * toNumber(c.quantity)); // netto pro Zeile
      return sum + nettoZeile;
    }, 0)
  ); // items net total (net total without shipping)

  const itemsVat = round2(itemsGrossSum - itemsNet); // USt auf Items

  const groups = new Map(); // vatRate: { itemsGross }

  // group cartItems by vatRate and sum gross per group (for proportional shipping split):
  for (const c of cartItems) {
    // go through each cartItem:
    const vat = Math.max(0, toNumber(c.vatRate));
    const qty = Math.max(0, Math.floor(toNumber(c.quantity) || 0));
    const unitGross = Math.max(0, toNumber(c.unitGross));

    const lineGross = round2(unitGross * qty); // calculate the gross for this line (cartItem)

    // add the calculated gross of the current line (cartItem) to its vatRate group:
    const g = groups.get(vat) || {
      vatRate: vat,
      itemsGross: 0,
    };
    g.itemsGross = round2(g.itemsGross + lineGross);
    groups.set(vat, g);
  }

  const groupsArr = Array.from(groups.values()); // convert the groups Map to an array
  const itemsGrossTotal = round2(
    groupsArr.reduce((s, g) => s + g.itemsGross, 0)
  ); // sum up all itemsGross values of groupsArr

  let shippingByVat = groupsArr.map((g) => {
    // go through each group of groupsArr:
    const share = itemsGrossTotal > 0 ? g.itemsGross / itemsGrossTotal : 0; // calculate the current groups gross share of the gross total
    const grossPart = round2(shippingGross * share); // this group's gross shipping portion

    return {
      vatRate: g.vatRate,
      itemsGross: g.itemsGross,
      shippingGross: grossPart,
      shippingNet: 0,
      shippingVat: 0,
    };
  }); // split the *gross shipping cost* across VAT groups proportionally to the gross value of items in each VAT group

  // remainder correction so that sum(shippingGross parts) == shippingGross exactly. This is needed, because we round each group’s shipping gross to 2 decimals (we might lose/gain 0.01). We fix that by assigning the remaining cents to one group (usually the group with the highest itemsGross):
  const sumShippingGrossParts = round2(
    shippingByVat.reduce((s, x) => s + x.shippingGross, 0)
  ); // sum up the shippingGross values of all groups
  let remainder = round2(shippingGross - sumShippingGrossParts); // calculate the remainder

  if (shippingByVat.length > 0 && remainder !== 0) {
    // if the remainder !== 0
    let idx = 0;

    for (let i = 1; i < shippingByVat.length; i++) {
      // go through all groups:
      if (shippingByVat[i].itemsGross > shippingByVat[idx].itemsGross) idx = i; // keep track of the group with the largest itemsGross value
    }

    shippingByVat[idx].shippingGross = round2(
      shippingByVat[idx].shippingGross + remainder
    ); // add the remainder to the shippingGross of the group with the largest itemsGross value
  }

  shippingByVat = shippingByVat.map((x) => {
    // go through each group of shippingByVat:
    const net = grossToNet(x.shippingGross, x.vatRate); // compute the shippingNet for the current group
    const vat = round2(x.shippingGross - net); // compute the shippingVat for the current group

    // update the current group with the computed shippingNet and shippingVat values:
    return {
      ...x,
      shippingNet: net,
      shippingVat: vat,
    };
  });

  const shippingNetTotal = round2(
    shippingByVat.reduce((s, x) => s + x.shippingNet, 0)
  ); // calculate the total net portion of the shipping (sum up the shippingNet values of all groups)

  const shippingVat = round2(shippingGross - shippingNetTotal); // calculate the total vat for the shipping (total VAT as rest)

  const shippingNet = shippingNetTotal; // total net shipping
  const shippingByVatRate = shippingByVat.map((x) => ({
    vatRate: x.vatRate,
    gross: x.shippingGross,
    net: x.shippingNet,
    vat: x.shippingVat,
  }));

  const totalNet = round2(itemsNet + shippingNet); // Netto Gesamt (Summe der gerundeten Netto-Zeilen + Netto Versand)

  const totalVat = round2(totalGross - totalNet); // Steuer Gesamt (als Rest, damit Brutto/Netto immer exakt zusammenpassen)

  return {
    itemsGross: itemsGrossSum,
    shippingGross,
    totalGross,
    itemsNet,
    itemsVat,
    shippingVat,
    shippingNet,
    shippingByVatRate,
    totalNet,
    totalVat,
  };
}; // the order totals PlaceOrderScreen shows, charges via PayPal and sends to the backend

export const calculateLineTotals = (it) => {
  const qty = Math.max(0, Math.floor(Number(it.quantity) || 0));
  const unitGross = Math.max(0, Number(it.unitGross) || 0);
  const unitNet = Math.max(0, Number(it.unitNet) || 0);

  const lineGross = round2(unitGross * qty);
  const lineNet = round2(unitNet * qty);
  const lineVat = round2(lineGross - lineNet); // VAT as rest, so line always matches

  return {
    lineGross,
    lineNet,
    lineVat,
  };
}; // the lineGross, lineNet and lineVat fields of an order item

export const totalAfterRefund = (order) => order.totalGross + order.refundGross; // an order's total with its refund taken off (its `?? 0` went, Oct 2026, user decision: refundGross defaults to 0, and the order list's and the order page's routes send hydrated documents, so every order they send carries it) (refundGross is stored negative, orderModel.js): the order list's Total column and the order page's totals card (one expression in each until Oct 2026, lifted from OrderListScreen)
